import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Database, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export function RecoveryCenter() {
  const [drills, setDrills] = useState<any[]>([]);
  const [exports, setExports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const [drillRes, exportRes] = await Promise.all([
      supabase
        .from('restore_drills')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20),
      supabase
        .from('tenant_export_runs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20),
    ]);

    if (drillRes.error) toast.error('Failed to load drills');
    if (exportRes.error) toast.error('Failed to load exports');
    setDrills(drillRes.data || []);
    setExports(exportRes.data || []);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  const lastSuccessful = drills.find((d) => d.status === 'passed');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Database className="h-5 w-5 text-primary" />
          Recovery Center
        </h2>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Last Successful Drill</div>
            <div className="text-lg font-semibold mt-1">
              {lastSuccessful
                ? new Date(lastSuccessful.completed_at).toLocaleDateString()
                : 'Never'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Failed Drills</div>
            <div className="text-lg font-semibold mt-1 text-destructive">
              {drills.filter((d) => d.status === 'failed').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Total Exports</div>
            <div className="text-lg font-semibold mt-1">{exports.length}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Restore Drills</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Hash Match</TableHead>
                  <TableHead>Balance Match</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Completed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drills.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      <Badge variant={d.status === 'passed' ? 'default' : 'destructive'}>
                        {d.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {d.hash_matches ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-destructive" />
                      )}
                    </TableCell>
                    <TableCell>
                      {d.snapshot_balance_match ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-destructive" />
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {d.started_at ? new Date(d.started_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {d.completed_at ? new Date(d.completed_at).toLocaleString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {drills.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No restore drills recorded
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
